import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import FormTextarea from '../../../components/form/FormTextarea';
import FormNumberInput from '../../../components/form/FormNumberInput';
import CallexLoader from '../../../components/CallexLoader';

import {
  callexAiAgentDetailsGetById,
  callexAiAgentUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';

// Common destination countries with E.164 dial codes. The value stored is the
// dial code (e.g. "+91"); the telephony side prefixes it to the number.
export const COUNTRY_OPTIONS = [
  { value: '+91', label: 'India (+91)' },
  { value: '+1', label: 'USA / Canada (+1)' },
  { value: '+44', label: 'United Kingdom (+44)' },
  { value: '+971', label: 'UAE (+971)' },
  { value: '+61', label: 'Australia (+61)' },
  { value: '+65', label: 'Singapore (+65)' },
  { value: '+972', label: 'Israel (+972)' },
  { value: '+49', label: 'Germany (+49)' },
  { value: '+33', label: 'France (+33)' },
  { value: '+81', label: 'Japan (+81)' }
];

const CallexCallConfigModal = ({ isOpen, toggle, agent, onSaved }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    max_call_duration: 600,
    silence_timeout: 20,
    transfer_enabled: false,
    transfer_message: '',
    transfer_country: '+91',
    transfer_number: ''
  };

  const validationSchema = Yup.object({
    max_call_duration: Yup.number()
      .typeError('Enter a valid number')
      .required('Required')
      .min(30, 'Min 30s')
      .max(7200, 'Max 7200s'),
    silence_timeout: Yup.number()
      .typeError('Enter a valid number')
      .required('Required')
      .min(5, 'Min 5s')
      .max(600, 'Max 600s'),
    transfer_enabled: Yup.boolean(),
    transfer_message: Yup.string().when('transfer_enabled', {
      is: true,
      then: (schema) => schema.trim().required('Transfer message is required when transfer is enabled')
    }),
    transfer_number: Yup.string().when('transfer_enabled', {
      is: true,
      then: (schema) =>
        schema.trim().min(3, 'Enter a valid number').required('Destination number is required')
    })
  });

  const onSubmit = async (values, { setSubmitting }) => {
    const enabled = values.transfer_enabled === true;
    // Call-config lives on the agent record, so this is a partial update to
    // PUT /callex-bot-agent/{id} — only the call-config keys are sent; the
    // backend leaves the agent's other fields untouched.
    const payload = {
      id: agent?.id,
      max_call_duration: Number(values.max_call_duration),
      silence_timeout: Number(values.silence_timeout),
      transfer_enabled: enabled,
      // When transfer is off, clear the transfer detail so a stale number can't
      // fire. When on, send the trimmed values.
      transfer_message: enabled ? values.transfer_message.trim() : '',
      transfer_country: enabled ? values.transfer_country : '',
      transfer_number: enabled ? values.transfer_number.trim() : ''
    };

    try {
      const response = await dispatch(callexAiAgentUpdate(payload)).unwrap();
      if (response) {
        toast.success(response.message || 'Call configuration saved');
        toggle();
        onSaved?.();
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setSubmitting(false);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue,
    isSubmitting
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  // Fetch the full agent to prefill the current call-config values (the list
  // row is partial and does not carry these fields).
  useEffect(() => {
    if (!isOpen || !agent?.id) return;
    let cancelled = false;
    setLoading(true);
    dispatch(callexAiAgentDetailsGetById(agent.id))
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res ?? {};
        setValues({
          max_call_duration: data.max_call_duration ?? 600,
          silence_timeout: data.silence_timeout ?? 20,
          transfer_enabled: data.transfer_enabled === true,
          transfer_message: data.transfer_message ?? '',
          transfer_country: data.transfer_country || '+91',
          transfer_number: data.transfer_number ?? ''
        });
      })
      .catch((error) => console.log('error', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, agent?.id]);

  const handleClose = () => {
    toggle();
    resetForm();
  };

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">
          Call Configuration{agent?.name ? ` — ${agent.name}` : ''}
        </div>
      </ModalHeader>
      <ModalBody className="px-4">
        {loading ? (
          <div
            className="p-0 d-flex justify-content-center align-items-center m-0"
            style={{ height: '260px' }}>
            <CallexLoader />
          </div>
        ) : (
          <form id="callex-call-config-form" onSubmit={handleSubmit}>
            {/* SECTION 1: End Call Rules */}
            <div className="fw-bold text-muted mb-2 text-uppercase" style={{ fontSize: '0.75rem' }}>
              End Call Rules
            </div>
            <Row>
              <Col lg="6">
                <FormNumberInput
                  name="max_call_duration"
                  label="Max Call Duration (seconds)"
                  placeholder="600"
                  value={values.max_call_duration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="6">
                <FormNumberInput
                  name="silence_timeout"
                  label="Silence Timeout (seconds)"
                  placeholder="20"
                  value={values.silence_timeout}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>

            <hr className="my-3" />

            {/* SECTION 2: Transfer Call */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-bold text-muted text-uppercase" style={{ fontSize: '0.75rem' }}>
                Transfer Call
              </div>
              <FormToggle
                name="transfer_enabled"
                isShowLabel={false}
                trueName="Enabled"
                falseName="Disabled"
                value={String(values.transfer_enabled)}
                onChange={() => setFieldValue('transfer_enabled', !values.transfer_enabled)}
              />
            </div>

            {values.transfer_enabled ? (
              <Row>
                <Col lg="12">
                  <FormTextarea
                    name="transfer_message"
                    label="Transfer Message"
                    placeholder="Please hold while I connect you to an agent."
                    rows={2}
                    value={values.transfer_message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="transfer_country"
                    label="Country"
                    placeholder="Select country"
                    options={COUNTRY_OPTIONS}
                    value={values.transfer_country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="8">
                  <FormInput
                    name="transfer_number"
                    label="Destination Number"
                    placeholder="9876543210"
                    value={values.transfer_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              </Row>
            ) : (
              <div className="text-muted small">
                Transfer is disabled. The agent will not transfer calls.
              </div>
            )}
          </form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={handleClose}
          className="px-5 bg-transparent text-primary"
          disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          form="callex-call-config-form"
          type="submit"
          className="px-5 bg-primary border-primary"
          disabled={isSubmitting || loading}>
          {isSubmitting ? 'Saving...' : 'Save Configuration'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CallexCallConfigModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  agent: PropTypes.object,
  onSaved: PropTypes.func
};

export default CallexCallConfigModal;
